import { formatEther, parseEther, parseUnits } from "viem";
import { ChainTag, getChain } from "./chains";


/**
 * Format a bigint quantity of a given chain to a human readable
 * number based on the chain's decimals.
 * @example formatDenom(1000000000000000000n, "BASE") => 1
 * @example formatDenom(499088764800000000n, "BASE", 4) => 0,4991
 *
 * @param value
 * @param chainTag
 * @param toFixed
 * @returns
 */
export const formatDenom = (value: bigint | number, chainTag: ChainTag, toFixed?: number) => {
  const chain = getChain(chainTag)!;
  toFixed = toFixed || chain.currency.decimals;
  if (chain.arch === "EVM") return parseFloat(Number(formatEther(BigInt(value))).toFixed(toFixed));
  else return parseFloat((Number(value) / 10 ** chain.currency.decimals).toFixed(toFixed));
};


/**
 * Parse a human readable number to a bigint quantity of a given chain
 * based on the chain's decimals.
 * @example parseDenom(1, "BASE") => 1000000000000000000n
 *
 * @param amount
 * @param chainTag
 * @returns
 */
export const parseDenom = (amount: number | string, chainTag: ChainTag) => {
  const chain = getChain(chainTag);
  if (chain.arch === "EVM") return parseEther(amount.toString());
  else return BigInt(Math.floor(parseFloat(amount as string) * (10 ** chain.currency.decimals)));
};

// USDC is 6 decimals and 1:1 with USD.
export const usdToUsdc = (usd: number) => parseUnits(usd.toFixed(6), 6);


export const bigIntMax = (...args: bigint[]) => args.reduce((m, e) => e > m ? e : m);
export const bigIntMin = (...args: bigint[]) => args.reduce((m, e) => e < m ? e : m);
export const bigIntMedian = (data: bigint[]): bigint => {
  const sorted = [...data].sort((a, b) => Number(a - b));
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2n
    : sorted[mid];
};

export const stringifyBigIntObj = (obj: any) => JSON.stringify(obj, (_, v) => typeof v === "bigint" ? v.toString() : v);

/**
 * Recursively convert bigint values to strings for safe JSON storage.
 * Works for any value: primitives, arrays, objects, etc.
 * @param value
 * @returns
 */
export const serializeBigIntKeys = <T>(value: T): T => {
  if (typeof value === "bigint") return value.toString() as T;
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = serializeBigIntKeys(item);
      if (next !== item) changed = true;
      return next;
    });
    return (changed ? out : value) as T;
  }
  let changed = false;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    const next = serializeBigIntKeys(v);
    if (next !== v) changed = true;
    out[k] = next;
  }
  return (changed ? out : value) as T;
};


/**
 * Binary search for the maximum value in a range that satisfies a predicate.
 * @param low
 * @param high
 * @param predicate
 * @returns
 */
export const bigintBinarySearchMax = (
  low: bigint,
  high: bigint,
  predicate: (value: bigint) => boolean
): bigint => {
  while (low < high) {
    const mid = low + (high - low + 1n) / 2n;
    if (predicate(mid)) {
      low = mid;
    } else {
      high = mid - 1n;
    }
  }
  return low;
};


/**
 * Add a percentage margin to a bigint value
 * @param value
 * @param percentage
 * @returns
 */
export const bufferBigint = (value: bigint, bps: number) => {
  const buffer = (value * BigInt(bps)) / 10000n;
  return value + buffer;
};


export const clamp0 = (value: bigint) => (value < 0n ? 0n : value);
